// frontend/src/hooks/useAdminFeedbackReset.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import type { SignalCount } from "@/hooks/useAdminFeedback";

export interface FeedbackResetPreview {
  total: number;
  by_signal_type: SignalCount[];
  earliest: string | null; // ISO 8601
  latest: string | null;
}

export interface FeedbackResetResult {
  deleted: number;
  by_signal_type: SignalCount[];
}

/**
 * Preview rows that would be deleted at the given scope.
 * - Pass a userId string for per-user scope.
 * - Pass `"all"` for all-users scope (global preview).
 * - Pass `null` or `undefined` to keep the query disabled (e.g., when the
 *   modal containing this hook is closed — avoids firing a destructive-
 *   preview request just by mounting the component).
 */
export function useAdminFeedbackResetPreview(
  scope: string | null | undefined,
) {
  const enabled = scope !== null && scope !== undefined;
  const isAll = scope === "all";
  const url = isAll
    ? "api/admin/feedback-reset-preview"
    : `api/admin/users/${scope}/feedback-reset-preview`;

  return useQuery<FeedbackResetPreview>({
    queryKey: isAll
      ? ["admin", "feedback-reset-preview", "all"]
      : ["admin", "feedback-reset-preview", "user", scope],
    queryFn: () => apiClient.get(url).json<FeedbackResetPreview>(),
    staleTime: 10_000,
    retry: false,
    enabled,
  });
}

export function useAdminFeedbackResetUser() {
  const qc = useQueryClient();
  return useMutation<FeedbackResetResult, Error, string>({
    mutationFn: (userId) =>
      apiClient
        .delete(`api/admin/users/${userId}/feedback-events`, {
          json: { confirm: true },
        })
        .json<FeedbackResetResult>(),
    onSettled: (_data, _err, userId) => {
      qc.invalidateQueries({ queryKey: ["admin", "feedback-metrics"] });
      // Journey/milestone pills are derived from feedback_events MIN(created_at)
      // via useAdminJourney (queryKey ["admin","journey",userId]).
      qc.invalidateQueries({ queryKey: ["admin", "journey", userId] });
      qc.invalidateQueries({
        queryKey: ["admin", "feedback-reset-preview", "user", userId],
      });
      qc.invalidateQueries({ queryKey: ["admin", "user-detail", userId] });
    },
  });
}

export function useAdminFeedbackResetAll() {
  const qc = useQueryClient();
  return useMutation<FeedbackResetResult, Error, void>({
    mutationFn: () =>
      apiClient
        .delete("api/admin/feedback-events", {
          json: { confirm: true, confirm_phrase: "RESET ALL FEEDBACK" },
        })
        .json<FeedbackResetResult>(),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["admin", "feedback-metrics"] });
      qc.invalidateQueries({ queryKey: ["admin", "feedback-reset-preview"] });
      // Invalidate all admin-journey queries (prefix match — every userId).
      qc.invalidateQueries({ queryKey: ["admin", "journey"] });
    },
  });
}
